import S from 'common/service/sanctuary';
import { intersperse, addIndex } from 'ramda';
import React from 'react';
import Chip from 'common/mdc/chip';
import Button from 'common/mdc/button';
import { FormattedMessage, defineMessages } from 'react-intl';
import Toolbar, { ToolbarRow, ToolbarSection } from 'common/mdc/toolbar';
import ScrollContainer from 'common/mdc/scrollbar/container';
import { func, bool, node } from 'prop-types';
import { types } from 'common/store/search/advanced/criteria';
import CriteriaType from './type';
import criteriaShape from './criteriaShape';


import { translateConstraint as translatePlainConstraint } from './plain';
import { translateConstraint as translateChoiceConstraint } from './choice';
import { translateConstraint as translateDateTimeConstraint } from './datetime';
import { translateConstraint as translateLocationConstraint } from './location';

const messages = defineMessages({
  clearAll: {
    id: 'nodes.clearAll',
    defaultMessage: 'Clear all',
  },
  addConstraint: {
    id: 'nodes.addConstraint',
    defaultMessage: 'Add constraint',
  },
  apply: {
    id: 'nodes.apply',
    defaultMessage: 'Apply',
  },
});

const mapIndexed = addIndex(S.map);

const translateCriteria = criteria => S.pipe([
  S.prop('constraints'),
  mapIndexed((constraint, idx) => S.map(
    translation => <b key={idx}>{translation}</b>,
    ({
      [types.CHOICE]: translateChoiceConstraint(criteria),
      [types.PLAIN]: translatePlainConstraint,
      [types.DATETIME]: translateDateTimeConstraint,
      [types.LOCATION]: translateLocationConstraint,
    })[criteria.type](constraint),
  )),
  S.justs,
], criteria);

export const DisplayCriteria = ({
  criteria,
  label,
}) => (
  <Chip className="search-criteria search-display-criteria">
    {label}
    &nbsp;-&nbsp;
    {intersperse(', ', translateCriteria(criteria))}
  </Chip>
);

const SearchCriteria = ({
  criteria,
  label,
  nullable,
  onRemove,
  onClearAll,
  onAddConstraint,
  onRemoveConstraint,
  hasConstraints,
  onOperatorChange,
  onValueChange,
  onApply,
}) => {
  const displayValues = translateCriteria(criteria);

  let menuWrapper = null;

  return (
    <Chip
      className="search-criteria"
      deletable={onRemove && S.is(Function, onRemove)}
      menuWrapperRef={(wrapper) => {
        menuWrapper = wrapper;
      }}
      onClose={onApply}
      content={[
        <form
          key="criteria-form"
          className="criteria-form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();

            if (menuWrapper) {
              menuWrapper.close();
            }
          }}
        >
          {!criteria.singleConstraint && (
          <Toolbar key="criteria-top-toolbar" className="search-criteria__toolbar">
            <ToolbarRow>
              <ToolbarSection align={ToolbarSection.ALIGN_START}>
                <Button type="button" onClick={() => onAddConstraint(criteria)}>
                  <FormattedMessage {...messages.addConstraint} />
                </Button>
              </ToolbarSection>
            </ToolbarRow>
          </Toolbar>)}
          <ScrollContainer>
            <div className="constraints-container">
              {criteria.constraints.map((constraint, idx) => (
                <CriteriaType
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${criteria.property}-${idx}`}
                  nullable={nullable}
                  label={label}
                  onRemove={() => onRemoveConstraint(criteria, idx)}
                  onOperatorChange={operator => onOperatorChange(criteria, idx, operator)}
                  onValueChange={value => onValueChange(criteria, idx, value)}
                  constraint={constraint}
                  removable={hasConstraints}
                  criteria={criteria}
                />
              ))}
            </div>
          </ScrollContainer>
          <Toolbar key="criteria-bottom-toolbar" className="search-criteria__toolbar">
            <ToolbarRow>
              <ToolbarSection align={ToolbarSection.ALIGN_END}>
                {!criteria.singleConstraint && (
                <Button
                  key="reset"
                  type="reset"
                  disabled={!hasConstraints}
                  onClick={() => onClearAll(criteria)}
                >
                  <FormattedMessage {...messages.clearAll} />
                </Button>)}
                <Button
                  raised
                  disabled={!hasConstraints}
                  type="submit"
                  key="submit"
                >
                  <FormattedMessage {...messages.apply} />
                </Button>
              </ToolbarSection>
            </ToolbarRow>
          </Toolbar>
        </form>,
      ]}
      onRemove={onRemove}
    >{displayValues.length
      ? (
        <span>
          {label}
          &nbsp;-&nbsp;
          {intersperse(', ', displayValues)}
        </span>
      )
      : label
    }
    </Chip>
  );
};

SearchCriteria.propTypes = {
  criteria: criteriaShape.isRequired,
  label: node.isRequired,
  onRemove: func,
  onClearAll: func.isRequired,
  onAddConstraint: func.isRequired,
  onRemoveConstraint: func.isRequired,
  hasConstraints: bool.isRequired,
  onOperatorChange: func.isRequired,
  onValueChange: func.isRequired,
  onApply: func.isRequired,
};

export default SearchCriteria;
